import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fmtPct } from "../consts/demandplanning";
import { buildAiRebalanceRecommendations } from "../lib/aiRebalanceRecommendations";
import { countAiRebalanceGovernanceTiles } from "../lib/aiRebalanceGovernanceCounts";
import {
    buildForecastYearDataPatch,
    countClosedForecastMonths,
    enrichForecastTableSnapshotForDataColumn,
    normalizePlanningData,
    readForecastTableSnapshot,
    rebuildForecastTableSnapshotActualsFromApi,
    rebalanceCategoryPctRows,
    rebalanceSubCategoryPctRows,
} from "../lib/forecastPlanningDataColumn";
import {
    attachRebalanceRecommendations,
    buildPendingSkuRebalanceGroups,
    buildSkuRebalancePlanningRecords,
} from "../lib/generateRebalanceRecommendations";
import { adjustedFinalPercentToEligibleOverride } from "../lib/skuAllocationDerivedChecks";
import {
    DemandPlanningLoadingKeys,
    createAiRebalanceHistory,
    getAiRebalanceHistory,
    getForecastDemandPlanningData,
    getForecastVsActualsTableData,
    getSkuAllocationTableData,
    saveForecastDemandProductOverride,
    updateForecastDemandPlanning,
} from "../slices/demandPlanningSlice";

function resolveForecastYearStr(planningRow) {
    const year = planningRow?.forecast_year ?? planningRow?.forecastYear;
    return year != null && String(year).trim() !== "" ? String(year) : "";
}

function resolveApiJson(planningRow, forecastDemandPlanningData, planningId, forecastYearStr) {
    const byYear = normalizePlanningData(planningRow?.data);
    const bucketApi = byYear[forecastYearStr]?.api;
    if (bucketApi && typeof bucketApi === "object") {
        return structuredClone(bucketApi);
    }
    if (
        forecastDemandPlanningData &&
        String(forecastDemandPlanningData.id) === String(planningId) &&
        String(forecastDemandPlanningData.forecastYear) === String(forecastYearStr)
    ) {
        return structuredClone(forecastDemandPlanningData);
    }
    return null;
}

function normalizeFilterIds(value) {
    if (value == null || value === "") return [];
    return (Array.isArray(value) ? value : [value])
        .map((id) => String(id).trim())
        .filter(Boolean);
}

function resolveAllowedCategoryNames({
    selectedCategoryId,
    tableSnapshot,
    forecastDemandPlanningData,
}) {
    const categoryIds = normalizeFilterIds(selectedCategoryId);
    if (!categoryIds.length) return null;
    const idSet = new Set(categoryIds);
    const names = new Set();

    (forecastDemandPlanningData?.categories || []).forEach((row) => {
        if (idSet.has(String(row.categoryId ?? "")) && row.categoryName) {
            names.add(row.categoryName);
        }
    });
    (tableSnapshot?.categories || []).forEach((row) => {
        if (idSet.has(String(row.categoryId ?? "")) && row.category) {
            names.add(row.category);
        }
    });
    return names;
}

function resolveAllowedSubCategoryNames({
    selectedSubcategoryId,
    tableSnapshot,
    forecastDemandPlanningData,
}) {
    const subcategoryIds = normalizeFilterIds(selectedSubcategoryId);
    if (!subcategoryIds.length) return null;
    const idSet = new Set(subcategoryIds);
    const names = new Set();

    (forecastDemandPlanningData?.subcategories || []).forEach((row) => {
        if (idSet.has(String(row.subcategoryId ?? "")) && row.subcategoryName) {
            names.add(row.subcategoryName);
        }
    });
    (tableSnapshot?.subCategories || []).forEach((row) => {
        if (idSet.has(String(row.id ?? row.subcategoryId ?? "")) && row.sub_category) {
            names.add(row.sub_category);
        }
    });
    return names;
}

function filterSkuRowsForRebalanceView(
    items,
    {
        tableSnapshot,
        forecastDemandPlanningData,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        searchTerm,
    }
) {
    const statusIds = normalizeFilterIds(selectedStatusId);
    const q = String(searchTerm ?? "").trim().toLowerCase();
    const allowedCategories = resolveAllowedCategoryNames({
        selectedCategoryId,
        tableSnapshot,
        forecastDemandPlanningData,
    });
    const allowedSubCategories = resolveAllowedSubCategoryNames({
        selectedSubcategoryId,
        tableSnapshot,
        forecastDemandPlanningData,
    });

    return (items || []).filter((row) => {
        if (allowedCategories && !allowedCategories.has(row.category)) return false;
        if (allowedSubCategories && !allowedSubCategories.has(row.subCategory)) return false;
        if (statusIds.length && !statusIds.includes(String(row.status ?? "").trim())) {
            return false;
        }
        if (q) {
            const hay = [row.supplySku, row.productName, row.category, row.subCategory, row.status]
                .filter((part) => part != null && String(part).trim() !== "")
                .join(" ")
                .toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    });
}

export function useAiRebalanceForecast({
    selectedForecastDemandPlanning,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
    searchTerm,
    onServerRecordUpdated,
}) {
    const dispatch = useDispatch();
    const {
        forecastDemandPlanningData,
        aiRebalanceHistory,
        skuAllocationTableData,
        forecastVsActualsTableData,
        forecastCatalogGetError,
        loadingKeys,
    } = useSelector((state) => state.demandPlanning);
    const planningRowRef = useRef(selectedForecastDemandPlanning);
    const [tableSnapshot, setTableSnapshot] = useState(null);
    const [revenueTarget, setRevenueTargetState] = useState(0);

    const planningId = selectedForecastDemandPlanning?.id;
    const forecastYearStr = resolveForecastYearStr(selectedForecastDemandPlanning);
    const loadingForecast = loadingKeys.includes(DemandPlanningLoadingKeys.getForecastDemandPlanningData);
    const forecastActualsApiReady = useMemo(() => {
        if (!planningId || !forecastYearStr) return false;
        const api = forecastDemandPlanningData;
        return (
            api &&
            String(api.id) === String(planningId) &&
            String(api.forecastYear) === String(forecastYearStr) &&
            Array.isArray(api.channels) &&
            api.channels.length > 0
        );
    }, [forecastDemandPlanningData, forecastYearStr, planningId]);
    /** Blocks rebalance UI until `/forecast-demand-planning-data` returns fresh YTD actuals. */
    const loadingRebalanceActuals =
        loadingForecast ||
        (Boolean(planningId && forecastYearStr && tableSnapshot) &&
            !forecastActualsApiReady &&
            forecastCatalogGetError == null);
    const loadingHistory = loadingKeys.includes(DemandPlanningLoadingKeys.getAiRebalanceHistory);
    const saving =
        loadingKeys.includes(DemandPlanningLoadingKeys.updateForecastDemandPlanning) ||
        loadingKeys.includes(DemandPlanningLoadingKeys.createAiRebalanceHistory) ||
        loadingKeys.includes(DemandPlanningLoadingKeys.saveForecastDemandProductOverride);

    useEffect(() => {
        planningRowRef.current = selectedForecastDemandPlanning;
    }, [selectedForecastDemandPlanning]);

    const skuRebalanceCatalogQuery = useMemo(
        () => ({
            demandPlanningId: planningId,
            demandPlanningYear: Number(forecastYearStr) || forecastYearStr,
        }),
        [forecastYearStr, planningId]
    );

    useEffect(() => {
        if (!planningId || !forecastYearStr) return;
        dispatch(getForecastDemandPlanningData({ id: planningId, forecastYear: forecastYearStr }));
    }, [dispatch, planningId, forecastYearStr]);

    useEffect(() => {
        if (!planningId || !forecastYearStr) return;
        dispatch(getSkuAllocationTableData(skuRebalanceCatalogQuery));
        dispatch(getForecastVsActualsTableData(skuRebalanceCatalogQuery));
    }, [dispatch, forecastYearStr, planningId, skuRebalanceCatalogQuery]);

    useEffect(() => {
        if (!planningId || !forecastYearStr) return;
        dispatch(
            getAiRebalanceHistory({
                demandPlanningId: planningId,
                forecastYear: forecastYearStr,
            })
        );
    }, [dispatch, planningId, forecastYearStr]);

    useEffect(() => {
        if (!selectedForecastDemandPlanning || !forecastYearStr) {
            setTableSnapshot(null);
            setRevenueTargetState(0);
            return;
        }

        const snapshot = readForecastTableSnapshot(selectedForecastDemandPlanning, forecastYearStr);
        const revenue =
            selectedForecastDemandPlanning.revenue_target ??
            selectedForecastDemandPlanning.revenueTarget;
        setRevenueTargetState(Number(revenue ?? 0) || 0);
        setTableSnapshot(snapshot);
    }, [selectedForecastDemandPlanning, forecastYearStr]);

    const persistTableSnapshot = useCallback(
        async (nextTable, nextRevenueTarget = revenueTarget) => {
            if (!planningId || !forecastYearStr || !nextTable) return false;
            const apiJson = resolveApiJson(
                planningRowRef.current,
                forecastDemandPlanningData,
                planningId,
                forecastYearStr
            );
            if (!apiJson) {
                toast.error("Forecast catalog is not ready for this year yet.", {
                    position: "bottom-right",
                });
                return false;
            }

            const nextData = buildForecastYearDataPatch({
                existingData: planningRowRef.current?.data,
                forecastYearStr,
                tableSnapshot: nextTable,
                apiJson,
                revenueTarget: Number(nextRevenueTarget) || 0,
            });

            try {
                const payload = { data: nextData, silent: true };
                if (
                    Number(nextRevenueTarget) !==
                    Number(
                        planningRowRef.current?.revenue_target ??
                            planningRowRef.current?.revenueTarget ??
                            0
                    )
                ) {
                    payload.revenueTarget = Number(nextRevenueTarget) || 0;
                }

                const res = await dispatch(
                    updateForecastDemandPlanning({
                        id: planningId,
                        ...payload,
                    })
                ).unwrap();
                const item = res?.item;
                if (item) {
                    planningRowRef.current = item;
                    onServerRecordUpdated?.(item);
                }

                const enriched = enrichForecastTableSnapshotForDataColumn({
                    channels: nextTable.channels,
                    categories: nextTable.categories,
                    subCategories: nextTable.subCategories,
                    months: nextTable.months,
                    revenueTarget: Number(nextRevenueTarget) || 0,
                    forecastYearStr,
                });
                setTableSnapshot({
                    channels: structuredClone(enriched.channels),
                    categories: structuredClone(enriched.categories),
                    subCategories: structuredClone(enriched.subCategories),
                    months: structuredClone(enriched.months),
                });
                setRevenueTargetState(Number(nextRevenueTarget) || 0);
                return true;
            } catch (error) {
                toast.error(
                    typeof error === "string" ? error : "Could not save AI rebalance changes.",
                    { position: "bottom-right" }
                );
                return false;
            }
        },
        [
            dispatch,
            forecastDemandPlanningData,
            forecastYearStr,
            onServerRecordUpdated,
            planningId,
            revenueTarget,
        ]
    );

    const applyRevenueTarget = useCallback(
        async (value) => {
            if (!tableSnapshot) return false;
            const nextRevenue = Number(value) || 0;
            const enriched = enrichForecastTableSnapshotForDataColumn({
                ...tableSnapshot,
                revenueTarget: nextRevenue,
                forecastYearStr,
            });
            const nextTable = {
                channels: enriched.channels,
                categories: enriched.categories,
                subCategories: enriched.subCategories,
                months: enriched.months,
            };
            return persistTableSnapshot(nextTable, nextRevenue);
        },
        [forecastYearStr, persistTableSnapshot, tableSnapshot]
    );

    const applyRebalanceCategoryPct = useCallback(
        async (category, pct) => {
            if (!tableSnapshot) return false;
            const nextCategories = rebalanceCategoryPctRows(tableSnapshot.categories, category, pct);
            const nextTable = {
                ...tableSnapshot,
                categories: nextCategories,
            };
            return persistTableSnapshot(nextTable, revenueTarget);
        },
        [persistTableSnapshot, revenueTarget, tableSnapshot]
    );

    const applyRebalanceSubCategoryPct = useCallback(
        async (id, pct) => {
            if (!tableSnapshot) return false;
            const nextSubCategories = rebalanceSubCategoryPctRows(
                tableSnapshot.subCategories,
                tableSnapshot.categories,
                id,
                pct
            );
            const nextTable = {
                ...tableSnapshot,
                subCategories: nextSubCategories,
            };
            return persistTableSnapshot(nextTable, revenueTarget);
        },
        [persistTableSnapshot, revenueTarget, tableSnapshot]
    );

    const recordDecision = useCallback(
        async (entry) => {
            if (!planningId || !forecastYearStr) return false;
            try {
                await dispatch(
                    createAiRebalanceHistory({
                        demandPlanningId: planningId,
                        forecastYear: forecastYearStr,
                        recommendationId: entry.recommendationId,
                        groupName: entry.groupName,
                        canonicalName: entry.canonicalName,
                        subcategory: entry.subcategory,
                        description: entry.description,
                        plannerContext: entry.plannerContext,
                        beforeAfter: entry.beforeAfter,
                        nextPct: entry.nextPct,
                        isApproved: entry.isApproved,
                        isDissmissed: entry.isDissmissed,
                        actedAt: new Date().toISOString(),
                    })
                ).unwrap();
                return true;
            } catch {
                return false;
            }
        },
        [dispatch, forecastYearStr, planningId]
    );

    const decisionLogs = aiRebalanceHistory;

    const appliedDecisions = useMemo(() => {
        const lookup = new Map();
        (aiRebalanceHistory || []).forEach((entry) => {
            const recommendationId = entry?.recommendationId ?? entry?.recommendation_id;
            if (!recommendationId) return;
            if (!lookup.has(recommendationId)) {
                lookup.set(recommendationId, entry);
            }
        });
        return lookup;
    }, [aiRebalanceHistory]);

    const processedSkuRecommendationIds = useMemo(() => {
        const ids = new Set();
        (aiRebalanceHistory || []).forEach((entry) => {
            const recommendationId = entry?.recommendationId ?? entry?.recommendation_id;
            if (!recommendationId) return;
            if (entry?.isApproved || entry?.isDissmissed || entry?.status) {
                ids.add(String(recommendationId));
            }
        });
        return ids;
    }, [aiRebalanceHistory]);

    const scopedCategories = useMemo(() => {
        if (!tableSnapshot?.categories?.length) return new Set();
        const allowed = resolveAllowedCategoryNames({
            selectedCategoryId,
            tableSnapshot,
            forecastDemandPlanningData,
        });
        if (allowed) return allowed;
        return new Set(tableSnapshot.categories.map((row) => row.category));
    }, [forecastDemandPlanningData, selectedCategoryId, tableSnapshot]);

    const scopedSubCategories = useMemo(() => {
        if (!tableSnapshot?.subCategories?.length) return new Set();
        const allowed = resolveAllowedSubCategoryNames({
            selectedSubcategoryId,
            tableSnapshot,
            forecastDemandPlanningData,
        });
        if (allowed) return allowed;
        return new Set(tableSnapshot.subCategories.map((row) => row.sub_category));
    }, [forecastDemandPlanningData, selectedSubcategoryId, tableSnapshot]);

    const categoryLtmByName = useMemo(() => {
        const map = new Map();
        (forecastDemandPlanningData?.categories || []).forEach((row) => {
            const pct =
                row.ltmPercentage != null && Number.isFinite(Number(row.ltmPercentage))
                    ? Number(row.ltmPercentage) / 100
                    : null;
            if (pct != null) map.set(row.categoryName, pct);
        });
        return map;
    }, [forecastDemandPlanningData]);

    const subCategoryLtmById = useMemo(() => {
        const map = new Map();
        (forecastDemandPlanningData?.subcategories || []).forEach((row) => {
            const pct =
                row.ltmPercentage != null && Number.isFinite(Number(row.ltmPercentage))
                    ? Number(row.ltmPercentage) / 100
                    : null;
            if (pct != null) map.set(String(row.subcategoryId), pct);
        });
        return map;
    }, [forecastDemandPlanningData]);

    const closedMonths = useMemo(
        () => countClosedForecastMonths(tableSnapshot?.months || [], forecastYearStr),
        [forecastYearStr, tableSnapshot]
    );

    /** Live actuals from forecast-demand API; keeps saved % overrides from persisted table snapshot. */
    const rebalanceTableSnapshot = useMemo(() => {
        if (!tableSnapshot) return null;

        const api = forecastDemandPlanningData;
        const apiMatchesPlanning =
            api &&
            String(api.id) === String(planningId) &&
            String(api.forecastYear) === String(forecastYearStr) &&
            Array.isArray(api.channels) &&
            api.channels.length > 0;

        const baseTable = apiMatchesPlanning
            ? rebuildForecastTableSnapshotActualsFromApi(api, tableSnapshot, forecastYearStr)
            : tableSnapshot;

        const enriched = enrichForecastTableSnapshotForDataColumn({
            ...baseTable,
            revenueTarget,
            forecastYearStr,
        });

        return {
            channels: enriched.channels,
            categories: enriched.categories,
            subCategories: enriched.subCategories,
            months: enriched.months,
        };
    }, [
        forecastDemandPlanningData,
        forecastYearStr,
        planningId,
        revenueTarget,
        tableSnapshot,
    ]);

    const allTableRecords = useMemo(() => {
        const allocationItems = skuAllocationTableData?.items || [];
        if (!allocationItems.length) {
            return [];
        }
        const forecastItems = forecastVsActualsTableData?.items || [];
        return attachRebalanceRecommendations(
            buildSkuRebalancePlanningRecords(
                allocationItems,
                forecastItems,
                skuAllocationTableData?.subcategoryEligibleTotalsBySubCategory
            )
        );
    }, [forecastVsActualsTableData, skuAllocationTableData]);

    const rebalanceViewFilter = useMemo(
        () => ({
            tableSnapshot,
            forecastDemandPlanningData,
            selectedCategoryId,
            selectedSubcategoryId,
            selectedStatusId,
            searchTerm,
        }),
        [
            forecastDemandPlanningData,
            searchTerm,
            selectedCategoryId,
            selectedSubcategoryId,
            selectedStatusId,
            tableSnapshot,
        ]
    );

    const tableRecords = useMemo(
        () => filterSkuRowsForRebalanceView(allTableRecords, rebalanceViewFilter),
        [allTableRecords, rebalanceViewFilter]
    );

    const pendingSkuRebalanceGroups = useMemo(
        () => buildPendingSkuRebalanceGroups(tableRecords, processedSkuRecommendationIds),
        [processedSkuRecommendationIds, tableRecords]
    );

    const refreshSkuRebalanceTableData = useCallback(async () => {
        if (!planningId || !forecastYearStr) return;
        await Promise.all([
            dispatch(getSkuAllocationTableData(skuRebalanceCatalogQuery)).unwrap(),
            dispatch(getForecastVsActualsTableData(skuRebalanceCatalogQuery)).unwrap(),
        ]);
    }, [dispatch, forecastYearStr, planningId, skuRebalanceCatalogQuery]);

    const applySkuRebalance = useCallback(
        async ({
            skuId,
            nextAdjustedFinalPercent,
            subcategoryEligibleTotalPercent,
            offsetSkuId,
            offsetNextAdjustedFinalPercent,
            offsetSubcategoryEligibleTotalPercent,
            recommendationKind,
            nextEligibleOverridePercent,
        }) => {
            if (!planningId || !forecastYearStr) return false;
            const planningYear = Number(forecastYearStr) || forecastYearStr;
            try {
                if (recommendationKind === "normalizeTo100") {
                    await dispatch(
                        saveForecastDemandProductOverride({
                            demandPlanningId: planningId,
                            demandPlanningYear: planningYear,
                            productId: skuId,
                            field: "overridePercent",
                            value: nextEligibleOverridePercent,
                            changeSource: "ai_rebalance",
                        })
                    ).unwrap();
                } else {
                    await dispatch(
                        saveForecastDemandProductOverride({
                            demandPlanningId: planningId,
                            demandPlanningYear: planningYear,
                            productId: skuId,
                            field: "overridePercent",
                            value: adjustedFinalPercentToEligibleOverride(
                                nextAdjustedFinalPercent,
                                subcategoryEligibleTotalPercent
                            ),
                            changeSource: "ai_rebalance",
                        })
                    ).unwrap();
                    await dispatch(
                        saveForecastDemandProductOverride({
                            demandPlanningId: planningId,
                            demandPlanningYear: planningYear,
                            productId: offsetSkuId,
                            field: "overridePercent",
                            value: adjustedFinalPercentToEligibleOverride(
                                offsetNextAdjustedFinalPercent,
                                offsetSubcategoryEligibleTotalPercent
                            ),
                            changeSource: "ai_rebalance",
                        })
                    ).unwrap();
                }
                await refreshSkuRebalanceTableData();
                return true;
            } catch (error) {
                toast.error(
                    typeof error === "string"
                        ? error
                        : "Could not apply SKU rebalance overrides.",
                    { position: "bottom-right" }
                );
                return false;
            }
        },
        [dispatch, forecastYearStr, planningId, refreshSkuRebalanceTableData]
    );

    const recommendations = useMemo(() => {
        if (!rebalanceTableSnapshot) return null;
        return buildAiRebalanceRecommendations({
            revenueTarget,
            categories: rebalanceTableSnapshot.categories,
            subCategories: rebalanceTableSnapshot.subCategories,
            months: rebalanceTableSnapshot.months,
            closedMonths,
            scopedCategories,
            scopedSubCategories,
            categoryLtmByName,
            subCategoryLtmById,
            tableRecords,
            fmtPercent: fmtPct,
        });
    }, [
        categoryLtmByName,
        closedMonths,
        rebalanceTableSnapshot,
        revenueTarget,
        scopedCategories,
        scopedSubCategories,
        subCategoryLtmById,
        tableRecords,
    ]);

    const governanceCounts = useMemo(
        () =>
            countAiRebalanceGovernanceTiles({
                recommendations,
                appliedDecisions,
                pendingSkuRebalanceGroups,
                decisionLogs,
            }),
        [appliedDecisions, decisionLogs, pendingSkuRebalanceGroups, recommendations]
    );

    return {
        forecastYearStr,
        tableSnapshot,
        revenueTarget,
        closedMonths,
        recommendations,
        tableRecords,
        pendingSkuRebalanceGroups,
        governanceCounts,
        decisionLogs,
        appliedDecisions,
        loadingForecast,
        loadingRebalanceActuals,
        loadingHistory,
        saving,
        applyRevenueTarget,
        applyRebalanceCategoryPct,
        applyRebalanceSubCategoryPct,
        applySkuRebalance,
        recordDecision,
    };
}
